/**
 * Störungsseite — statt der Anmeldemaske, wenn der Server gerade gestört ist.
 *
 * ═══ 🔴 WARUM ES DIESE SEITE GIBT ═══════════════════════════════════════════
 * 📏 29.10.2026, 07:56–08:23 UTC: Prod-D1 weg ⇒ jeder Nutzer landete in der
 * Anmeldemaske, meldete sich an und landete wieder dort (Login-Schleife).
 * 🔑 Bei einer Störung gibt es nichts anzumelden — die Anmeldung ist heil.
 * Diese Seite sagt das und kehrt von selbst zurück, sobald der Server antwortet.
 *
 * Entscheid Justin 02.11.2026: alle 30 Sekunden selbst prüfen + Knopf
 * „Jetzt erneut versuchen“. Läuft ausserhalb der Shell, im Stil der Anmeldeseite.
 */

import { probeAuthStatus, type AuthProbe } from "../auth/auth-guard";
import { escapeHtml } from "../lib/html";

export const STOERUNG_INTERVALL_MS = 30_000;

export function stoerungHtml(grund: string, zuletzt: string): string {
  return `
    <div class="login-page">
      <div class="login-card" role="alert" aria-live="polite">
        <div class="login-brand">
          <img src="/logo.png" alt="CoTrainer" />
          <h1>CoTrainer Controlling</h1>
        </div>
        <p class="login-text"><strong>CoTrainer ist gerade gestört.</strong></p>
        <p class="login-text">
          Deine Anmeldung bleibt erhalten — du musst dich nicht neu anmelden.
          Wir versuchen es alle 30 Sekunden von selbst.
        </p>
        <button type="button" id="stoerung-erneut" class="login-btn"><span>Jetzt erneut versuchen</span></button>
        <p id="stoerung-stand" class="login-footnote">${stoerungStand(grund, zuletzt)}</p>
      </div>
    </div>
  `;
}

function stoerungStand(grund: string, zuletzt: string): string {
  return `Zuletzt geprüft: ${escapeHtml(zuletzt)} · ${escapeHtml(grund)}`;
}

/**
 * Eine Prüfung: antwortet der Server wieder (angemeldet ODER abgemeldet), lädt
 * die App neu und der normale Start entscheidet. Bleibt er gestört, nichts tun.
 */
export async function pruefeUndLade(
  pruefe: () => Promise<AuthProbe>,
  neuLaden: () => void
): Promise<AuthProbe> {
  const p = await pruefe();
  if (p.art !== "gestoert") neuLaden();
  return p;
}

/** Rendert die Seite und startet die Wiederholung. Gibt eine Stopp-Funktion zurück. */
export function renderStoerungPage(host: HTMLElement, grund: string): () => void {
  const jetzt = () => new Date().toLocaleTimeString("de-DE");
  host.innerHTML = stoerungHtml(grund, jetzt());

  const knopf = host.querySelector<HTMLButtonElement>("#stoerung-erneut");
  const stand = host.querySelector<HTMLElement>("#stoerung-stand");
  let laeuft = false;

  const versuch = async (): Promise<void> => {
    if (laeuft) return;
    laeuft = true;
    if (knopf) knopf.disabled = true;
    try {
      const p = await pruefeUndLade(probeAuthStatus, () => window.location.reload());
      if (p.art === "gestoert" && stand) stand.innerHTML = stoerungStand(p.grund, jetzt());
    } finally {
      laeuft = false;
      if (knopf) knopf.disabled = false;
    }
  };

  knopf?.addEventListener("click", () => void versuch());
  const timer = window.setInterval(() => void versuch(), STOERUNG_INTERVALL_MS);
  return () => window.clearInterval(timer);
}
